import {useState, useRef, ReactNode, useEffect, useContext} from 'react'
import Cropper from 'cropperjs'
import {DndContext, closestCenter, PointerSensor, useSensors, useSensor} from '@dnd-kit/core'
import {arrayMove} from '@dnd-kit/sortable'
import {SortableContext, horizontalListSortingStrategy, useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Paper,
    Grid2,
    IconButton,
    Container, CircularProgress, Tooltip,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import 'cropperjs/dist/cropper.css'
import {useTranslation} from "react-i18next"
import ProductContext from "../contexts/ProductContext.tsx";
import {IProduct} from "../models/IProduct.ts";
import TextForm from "./TextForm.tsx";
import axios, {AxiosResponse} from "axios";
import {useNavigate} from "react-router-dom";
import {Grid} from "@mui/system";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const SortableItem = ({id, children}: { id: string; children: ReactNode }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id})

    const style = {
        padding: 3,
        transform: CSS.Transform.toString(transform),
        touchAction: 'none',
        transition,
    }

    return (
        <Grid2 ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </Grid2>
    )
}

interface IProps {
    productMutation: any
    mutateProductError: any
    formData: IProduct | undefined
    submitText: string
    headlineText: string
}

const ProductForm = ({productMutation, mutateProductError, formData, submitText, headlineText}: IProps) => {

    const [croppedImages, setCroppedImages] = useState<any>([])
    const [currentImage, setCurrentImage] = useState<any>(null)
    const [isProcessing, setIsProcessing] = useState(false)
    const navigate = useNavigate()
    const imageElement = useRef<any>(null)
    const cropper = useRef<any>(null)
    const [product, setProduct] = useState<IProduct>(formData!)
    const {deleteImageMutation, deleteImageError, addImageMutation} = useContext(ProductContext)
    const [editGermanTranslation, setEditGermanTranslation] = useState<boolean>(false)
    const imagesToDelete: string[] = []
    const {t} = useTranslation()
    const textFormLabels = [
        `${t('NAME_LABEL_TEXT')}`,
        `${t('NAME_DE_LABEL_TEXT')}`,
        `${t('PRICE_LABEL_TEXT')}`,
        `${t('DESCRIPTION_LABEL_TEXT')}`,
        `${t('DESCRIPTION_DE_LABEL_TEXT')}`,
        `${t('MATERIALS')}`,
        `${t('MATERIAL_DE')}`,
        `${t('CARE_INSTRUCTIONS')}`,
        `${t('CARE_INSTRUCTIONS_DE')}`,
        `${t('CATEGORY_LABEL_TEXT')}`,
        `${t('QUANTITY_LABEL_TEXT')}`
    ]
    const shippingCostFormLabels = [`${t('SHIPPING_COST_DE_LABEL_TEXT')}`, `${t('SHIPPING_COST_EU_LABEL_TEXT')}`, `${t('SHIPPING_COST_INT_LABEL_TEXT')}`]
    const textFormData = {
        name: {value: product.name.en, required: true},
        nameDe: {value: product.name.de, required: true, hidden: !editGermanTranslation},
        price: {value: product.price, required: true},
        description: {value: product.description.en, required: true, multiline: true},
        descriptionDe: {value: product.description.de, required: true, multiline: true, hidden: !editGermanTranslation},
        materials: {value: product.materials.en, required: true, multiline: true},
        materialsDe: {value: product.materials.de, required: true, multiline: true, hidden: !editGermanTranslation},
        careInstructions: {value: product.careInstructions.en, required: true, multiline: true},
        careInstructionsDe: {
            value: product.careInstructions.de,
            required: true,
            multiline: true,
            hidden: !editGermanTranslation
        },
        category: {value: product.category, required: true},
        amount: {value: product.amount, required: true},
    }
    const shippingCostFormData = {
        shippingCosts0: {value: product.shippingCosts[0].cost, required: false},
        shippingCosts1: {value: product.shippingCosts[1].cost, required: false},
        shippingCosts2: {value: product.shippingCosts[2].cost, required: false},
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 10,
                tolerance: 5,
            },
        })
    );

    useEffect(() => {
        if (product.images.length > 0) {
            setCroppedImages(
                product.images.map((imgUrl: any) => ({
                    file: null,
                    url: imgUrl,
                    blob: null,
                    isNew: false,
                }))
            );
        }
    }, [product]);

    useEffect(() => {

    })

    const handleCheckBoxChange = async (e: any) => {
        const {name, checked} = e.target
        setProduct({...product, [name]: checked})
    }

    const handleTextFormChange = async (key: string, value: any) => {
        if (key === 'description') {
            setProduct({
                ...product, description: {
                    ...product.description,
                    en: value,
                }
            })
        } else if (key === 'descriptionDe') {
            setProduct({
                ...product, description: {
                    ...product.description,
                    de: value,
                }
            })
        } else if (key === 'name') {
            setProduct({
                ...product, name: {
                    ...product.name,
                    en: value,
                }
            })
        } else if (key === 'nameDe') {
            setProduct({
                ...product, name: {
                    ...product.name,
                    de: value,
                }
            })
        } else if (key === 'materials') {
            setProduct({
                ...product, materials: {
                    ...product.materials,
                    en: value,
                }
            })
        } else if (key === 'materialsDe') {
            setProduct({
                ...product, materials: {
                    ...product.materials,
                    de: value,
                }
            })
        } else if (key === 'careInstructions') {
            setProduct({
                ...product, careInstructions: {
                    ...product.careInstructions,
                    en: value,
                }
            })
        } else if (key === 'careInstructionsDe') {
            setProduct({
                ...product, careInstructions: {
                    ...product.careInstructions,
                    de: value,
                }
            })
        } else if (key.includes('shippingCosts')) {
            key = key.replace('shippingCosts', '')
            const updatedShippingCosts = product.shippingCosts
            updatedShippingCosts[key as unknown as number].cost = value
            setProduct({...product, shippingCosts: updatedShippingCosts})
        } else {
            setProduct({...product, [key]: value})
        }
    }

    const handleImageChange = (e: any) => {
        const file = e.target.files[0]
        setCurrentImage(file)

        const reader = new FileReader()
        reader.onload = () => {
            const image = imageElement.current
            if (image) {
                image.src = reader.result
                cropper.current = new Cropper(image, {
                    aspectRatio: 1,
                    viewMode: 1,
                    movable: true,
                    zoomable: true,
                })
            }
        }
        reader.readAsDataURL(file)
    }

    const handleCrop = () => {
        const canvas = cropper.current.getCroppedCanvas()
        canvas.toBlob((blob: any) => {
            const newCroppedImage = {
                file: currentImage,
                blob: blob,
                url: URL.createObjectURL(blob),
                isNew: true,
                type: 'image/jpeg',
            }

            setCroppedImages([...croppedImages, newCroppedImage])
            setCurrentImage(null)
            cropper.current.destroy()
        })
    }

    const handleAddImage = () => {
        document.getElementById('imageInput')?.click()
    }

    const handleRemoveImage = async (index: any) => {
        const imgUrl = croppedImages[index].url;
        const cleanedUrl = imgUrl.replace('/uploads/', '..');

        if (!croppedImages[index].isNew) {
            imagesToDelete.push(cleanedUrl)
        }

        const newImages = croppedImages.filter((_: any, i: any) => i !== index)
        setCroppedImages(newImages)
    }

    const handleDragEnd = ({active, over}: any) => {
        if (active.id !== over.id) {
            const oldIndex = croppedImages.findIndex((img: any) => img.url === active.id)
            const newIndex = croppedImages.findIndex((img: any) => img.url === over.id)
            setCroppedImages(arrayMove(croppedImages, oldIndex, newIndex))
        }
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault()
        setIsProcessing(true)

        const newImages = croppedImages.filter((image: any) => image.isNew)

        let imageUrls: any[] = []

        if (imagesToDelete.length > 0) {
            imagesToDelete.map(async (image) => {
                await deleteImageMutation.mutateAsync(image)
            })
        }

        if (newImages.length > 0) {
            const imageData = new FormData()
            newImages.forEach((image: any, index: number) => {
                if (image.blob) {
                    const file = new File(
                        [image.blob],
                        `image_${Date.now()}_${index}.jpg`, //
                        {type: 'image/jpeg'}
                    )
                    imageData.append('images', file)
                }
            })

            const result = await addImageMutation.mutateAsync(imageData)
            imageUrls = result.imageUrls
        }

        let deNameResponse;
        let deDescriptionResponse;
        let deMaterialsResponse;
        let deCareInstructionsResponse;

        if (!editGermanTranslation) {
            if (product.name.en !== formData!.name.en)
                deNameResponse = await axios.post(`${VITE_API_BASE_URL}/api/products/translate`, {
                    text: product.name.en,
                    targetLang: 'de'
                });

            if (product.description.en !== formData!.description.en)
                deDescriptionResponse = await axios.post(`${VITE_API_BASE_URL}/api/products/translate`, {
                    text: product.description.en,
                    targetLang: 'de'
                });

            if (product.materials.en !== formData!.materials.en)
                deMaterialsResponse = await axios.post(`${VITE_API_BASE_URL}/api/products/translate`, {
                    text: product.materials.en,
                    targetLang: 'de'
                });

            if (product.careInstructions.en !== formData!.careInstructions.en)
                deCareInstructionsResponse = await axios.post(`${VITE_API_BASE_URL}/api/products/translate`, {
                    text: product.careInstructions.en,
                    targetLang: 'de'
                });
        }

        const updatedProduct = {
            ...product,
            images: [...imageUrls, ...croppedImages.filter((img: any) => !img.isNew).map((img: any) => img.url)],
            name: {
                en: product.name.en,
                de: deNameResponse ? deNameResponse.data.translation : product.name.de,
            },
            description: {
                en: product.description.en,
                de: deDescriptionResponse ? deDescriptionResponse.data.translation : product.description.de,
            },
            materials: {
                en: product.materials.en,
                de: deMaterialsResponse ? deMaterialsResponse.data.translation : product.materials.de,
            },
            careInstructions: {
                en: product.careInstructions.en,
                de: deCareInstructionsResponse ? deCareInstructionsResponse.data.translation : product.careInstructions.de,
            }
        }

        await productMutation.mutateAsync(updatedProduct, {
            onSuccess: (response: AxiosResponse<any, any>) => {
                navigate(`/product/${response.data._id}`);
            },
            onError: () => {
                setIsProcessing(false)
            }
        });
    }

    return (
        <Container sx={{padding: '12px'}}>
            <Paper
                elevation={4}

                sx={{
                    maxWidth: '900px',
                    margin: 'auto',
                    padding: 2,
                }}
            >
                <form onSubmit={handleSubmit}>
                    <Grid2 container>
                        <Grid2
                            size={{xs: 12, md: 8}}
                            paddingLeft={3}
                            paddingBottom={1}
                        >
                            <Typography variant={'h5'}>
                                {headlineText}
                            </Typography>
                        </Grid2>
                        <Grid size={{xs: 12, md: 4}}
                              paddingLeft={3}
                              paddingBottom={1}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => editGermanTranslation ? setEditGermanTranslation(false) : setEditGermanTranslation(true)}
                            >
                                {editGermanTranslation ? t('AUTO_GERMAN_TRANSLATION') : t('EDIT_GERMAN_TRANSLATION')}
                            </Button>
                        </Grid>
                    </Grid2>
                    <TextForm formLabels={textFormLabels} formData={textFormData}
                              onChange={handleTextFormChange}/>
                    <TextForm formLabels={shippingCostFormLabels} formData={shippingCostFormData}
                              onChange={handleTextFormChange}/>
                    <Box sx={{paddingLeft: 4, paddingRight: 4, paddingBottom: 4}}>
                        <FormControlLabel

                            control={
                                <Checkbox
                                    checked={product.inStock}
                                    name="inStock"
                                    onChange={(e) => handleCheckBoxChange(e)}
                                />
                            }
                            label={t('IN_STOCK_LABEL_TEXT')}
                        />
                        <Typography variant="h6" gutterBottom>
                            {t('IMAGES_TEXT')}
                        </Typography>
                        {currentImage && (
                            <Box>
                                <img ref={imageElement} alt="Aktuelles Bild" style={{maxWidth: '100%'}}/>
                                <Button onClick={handleCrop} variant="contained" color="primary" sx={{mt: 2}}>
                                    {t('CROP_IMAGE_BUTTON_TEXT')}
                                </Button>
                            </Box>
                        )}
                        {croppedImages && (
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={croppedImages.map((img: any) => img.url)}
                                                 strategy={horizontalListSortingStrategy}>

                                    <Grid2 container>
                                        {croppedImages.map((image: any, index: any) => (
                                            <Grid2 key={image.url} size={{xs: 6, md: 4}}>
                                                <Box sx={{position: 'relative'}}>
                                                    <SortableItem id={image.url}>
                                                        {!image.isNew ? (
                                                                <img
                                                                    src={`${VITE_API_BASE_URL}/${image.url}`}
                                                                    alt={`Bild ${index + 1}`}
                                                                    style={{width: '100%', borderRadius: '8px'}}
                                                                />) :
                                                            <img
                                                                src={image.url}
                                                                alt={`Bild ${index + 1}`}
                                                                style={{width: '100%', borderRadius: '8px'}}
                                                            />
                                                        }


                                                    </SortableItem>
                                                    <Tooltip title={t("DELETE_IMAGE")}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveImage(index)
                                                            }}
                                                            sx={{position: 'absolute', top: 4, right: 4}}
                                                        >
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                {deleteImageError &&
                                                    <Typography>
                                                        {t('DELETE_IMAGE_ERROR_TEXT')}
                                                    </Typography>
                                                }
                                            </Grid2>
                                        ))}
                                    </Grid2>
                                </SortableContext>
                            </DndContext>
                        )}
                        <Button
                            startIcon={<AddPhotoAlternateIcon/>}
                            variant="outlined"
                            onClick={handleAddImage}
                            sx={{mt: 2}}
                        >
                            {t('ADD_IMAGE_BUTTON_TEXT')}
                        </Button>
                        <input
                            type="file"
                            id="imageInput"
                            accept="image/*"
                            style={{display: 'none'}}
                            onChange={handleImageChange}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{mt: 4}}
                            disabled={isProcessing}
                        >
                            {isProcessing ? <CircularProgress size={24}/> : submitText}
                        </Button>
                        {mutateProductError && (
                            <Typography
                                sx={{color: 'red'}} variant="body1"
                            >
                                {t('EDIT_PRODUCT_ERROR_TEXT')}
                            </Typography>
                        )}
                    </Box>
                </form>
            </Paper>
        </Container>
    )
}

export default ProductForm
